/* eslint-disable no-unsafe-optional-chaining */
import { getAppConfigValue } from '@elliemae/pui-app-sdk';
import {
  getLoanPurposeType,
  getPropertyType,
  getPropertyUsageType,
} from '@frontend/epc';
import { FloatResponse } from '@frontend/epc-services';
import { LoanProgram, AdjustmentType } from '@frontend/pricing-search';
import { DeepPartial, setSignificantDigits } from '@frontend/utils';
import { get, isEmpty, isNil, round, toNumber } from 'lodash';
import { NavigateFunction } from 'react-router-dom';
import { setLoanDataValue } from '../../view/components/Form/utils/common';
import { getBuySideQualification } from '../../common/Qualification/QualificationActions';
import {
  buildBuysideAdjustmentsWithConcessions,
  convertPriceToParBased,
} from '../../common/app-helpers';
import { rateSelectorBuySide } from '../../common/mocks/Buyside';
import {
  getClientSettingsData,
  getZeroBasedPricing,
} from '../../common/services/ClientSettingsServices';
import Common from '../../common/services/Common';
import { KPI, kpiEnd, kpiStart } from '../../common/services/kpiTracking';
import Session, {
  ALL_SUMMARY_PARAMS,
  BUYSIDE_LOAN_ID,
  ORIGIN_SOURCE,
  USER_TYPE,
  IS_DEBUG,
  IS_SELLSIDE,
  IS_BUYSIDE,
  IS_SCENARIO_MODE,
} from '../../common/services/Session';
import { epcService } from '../../common/services/epcService';
import {
  getEncompassSettings,
  rateSelector,
} from '../../common/services/lockSummaryServices';
import browserHistory from '../../common/utils/browserHistory';
import createAction from '../../common/utils/createAction';
import { ZeroBasedPricingData } from '../../common/utils/customHooks/useIsZeroBasedPricing';
import { truncateText } from '../../common/utils/helpers';
import { PATH_LOGIN, PATH_SEARCH_RESULTS } from '../../route/constants';
import { openGlobalErrorModalAction } from '../globalErrorModal/actionCreators';
import { setRedirectTabAction } from '../landing/actions';
import { setLockedProgramData } from '../origin/actions';
import { closeLoader, openLoader } from '../screenLoader/actions';
import {
  SELECT_PRICE,
  selectProduct,
  setClientSettingsDataAction,
  setCompareDataAction,
  setCurrentRateSelectorAction,
  setDiffElementsAction,
  setRateSelectorAction,
  setSettings,
  setStatusFail,
  setZeroBasedPricingDataAction,
} from './actions';
import {
  checkPricingStatus,
  findBestProgram,
  getAdjustmentCompare,
  getArmAdjustmentFields,
  getGoveFeesForFHAandVA,
  getGoveFeesForUSDA,
  getPropertyValues,
  getRiskAssessmentType,
  getSnapshotProgramInfo,
  getVaLoanData,
  parseOriginAdjustments,
  parseRateAdjustmentsValidate,
} from './helpers';

/**
 * Fetches rate selector data and handles error responses ('LockRates' call)
 * @param {*} dispatch - Redux dispatch function
 * @param {*} loanData - Loan data object
 * @param {LoanProgram[]} [loanPrograms] - Optional loan programs array
 * @returns {*} Rate selector results
 */
export const getRateSelector = async (
  dispatch,
  loanData,
  loanPrograms?: LoanProgram[],
  selectedMIAmount?: number | null,
  pricingScorePrograms?: LoanProgram[],
) => {
  const user = Session.get(USER_TYPE);
  const isScenarioFlow = Session.get(IS_SCENARIO_MODE);
  const results = await rateSelector({
    loanData,
    loanPrograms,
    isScenarioFlow,
    selectedMIAmount,
    pricingScorePrograms,
  });

  if (results && results.code !== undefined) {
    if (user && user.userType === 'StandAlone' && !Session.get(IS_DEBUG)) {
      browserHistory.push(PATH_LOGIN);
    } else if (
      results.errors &&
      results.errors.find((o) => o.code === 'PS031') &&
      results.summary &&
      results.summary.includes('EPPS90010')
    ) {
      const summaryResponse = JSON.parse(results.summary);
      dispatch(
        openGlobalErrorModalAction({
          errorMessage: summaryResponse.summary,
          onCloseCallback: () => {
            dispatch(setRedirectTabAction(PATH_SEARCH_RESULTS));
          },
        }),
      );
    } else if (
      results.errors &&
      results.errors.find((o) => o.code === 'PS095')
    ) {
      dispatch(
        openGlobalErrorModalAction({
          errorMessage:
            'Your ICE PPE session has expired. Please close ICE PPE and log back in.',
          onCloseCallback: () => Common.closeApplication(),
        }),
      );
    } else {
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }),
      );
    }
  }
  return results;
};

export const getRateSelectorAction = () => async (dispatch, state) => {
  try {
    const loanData = get(state(), 'EPPS.loanInfo') || {};
    // Sometimes, loanInfo.loanId isn't populated, so we have to find it from other objects.
    const loanId =
      get(state(), 'EPPS.loanInfo.loanInformation.loanId') ||
      get(state(), 'epps.lockSummary.loanId');
    loanData.loanInformation = loanData.loanInformation || {};
    loanData.loanInformation.loanId = loanId;
    const loanPrograms = get(state(), 'EPPS.loanPrograms.loanPrograms', []);
    const selectedMIAmount = get(
      state(),
      'epps.lockSummary.selectedMIAmount',
      null,
    );
    const results = await getRateSelector(
      dispatch,
      loanData,
      loanPrograms,
      selectedMIAmount,
    );
    dispatch(setRateSelectorAction(results));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    dispatch(
      openGlobalErrorModalAction({
        onCloseCallback: () => Common.closeApplication(),
      }),
    );
  }
};

export const getClientSettingSelector = () => async (dispatch) => {
  try {
    const clientSettingsData = await getClientSettingsData();
    dispatch(setClientSettingsDataAction(clientSettingsData));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    dispatch(
      openGlobalErrorModalAction({
        onCloseCallback: () => Common.closeApplication(),
      }),
    );
  }
};

export const getZeroBasedPricingSelector =
  (loanChannel: string | null) => async (dispatch) => {
    const channelMapping = {
      'Banked - Retail': 1,
      'Banked - Wholesale': 2,
      Broker: 2, // TPOC treats Broker as Wholesale
      Brokered: 3,
      Correspondent: 4,
    };
    const mappedChannel = loanChannel ? channelMapping[loanChannel] : undefined;
    try {
      // bydefault par pricing is 100
      let zeroBasedPricingData: ZeroBasedPricingData = {
        parPricingType: 'HundredBased',
      };
      if (mappedChannel === 1 || mappedChannel === 2) {
        zeroBasedPricingData = (await getZeroBasedPricing(
          mappedChannel,
        )) as ZeroBasedPricingData;
      }
      dispatch(setZeroBasedPricingDataAction(zeroBasedPricingData));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }),
      );
    }
  };

export const getValidateDataAction =
  (navigate: NavigateFunction, isZeroBasedPricing: boolean) =>
  async (dispatch, state) => {
    const loanData = get(state(), 'EPPS.loanInfo') || {};
    const originalLoanInfo = get(state(), 'EPPS.originalLoanInformation') || {};
    const { relockRequest, additionalLockInfo } = loanData;
    const { specificRateDataID, specificProgramID } = relockRequest;
    const { lockRequests } = originalLoanInfo;
    const snapshotProgramName = getSnapshotProgramInfo(lockRequests);
    const loanFieldComparisson = Object.values(
      additionalLockInfo?.lockCompareResults || {},
    ).flat() as any[];
    const rateChange = loanFieldComparisson.find(({ id }) => id === 'rate');
    const { proposed: proposedRate } = rateChange || {};
    const changedNoteRate = !!proposedRate;
    loanData.loanInformation.target = convertPriceToParBased(
      isZeroBasedPricing,
      loanData.loanInformation.target,
      true,
      loanData,
    );
    try {
      dispatch(openLoader('Loading'));
      const pricing = await getBuySideQualification(
        'historical',
        loanData,
        dispatch,
      );
      const {
        program: selectedProgram,
        rateDataID: newRateDataID,
        status,
      } = checkPricingStatus(
        specificProgramID,
        specificRateDataID,
        proposedRate,
        pricing,
        changedNoteRate,
      );
      dispatch(
        setLockedProgramData({
          programName: selectedProgram?.program || snapshotProgramName,
          programID: specificProgramID,
          notes: selectedProgram?.notes?.replace(/\{.*?\}/g, '') || '',
          status,
          modalOpen: true,
        }),
      );

      if (status === 'eligible') {
        const newLoanData = {
          ...loanData,
          relockRequest: {
            ...relockRequest,
            specificRateDataID: newRateDataID,
          },
        };
        const debugBuyside = getAppConfigValue<boolean>(
          'partner-ui.debugBuyside',
        );
        const loanPrograms = get(state(), 'EPPS.loanPrograms.loanPrograms', []);

        const summary = debugBuyside
          ? rateSelectorBuySide
          : await getRateSelector(
              dispatch,
              newLoanData,
              loanPrograms,
              undefined,
              pricing?.loanPrograms,
            );
        dispatch(setRateSelectorAction(summary));

        if (!debugBuyside) {
          dispatch(selectProduct(selectedProgram));
        }
        if (
          loanFieldComparisson.length > 0 ||
          newRateDataID !== specificRateDataID
        ) {
          const originAdjustments = parseOriginAdjustments(originalLoanInfo);
          const summaryLienAdj = summary?.lien?.adjustments || [];
          const filteredSummaryLienAdj = summaryLienAdj.filter(
            (adj) => adj.adjustmentType === 'Adjustment',
          );
          // matchedCustomAdjustments are adjustments which are of type 'Adjustment' from origin
          // if you add custom adjustment with rate it will create adjustment of type 'Adjustment' and not 'CustomPriceAdjsutment'
          const matchedCustomAdjustments = [] as any[];
          const unmatchedAdjustments = [] as any[];
          originAdjustments.forEach((adj) => {
            if (
              adj.adjustmentType === 'Adjustment' &&
              adj?.rate &&
              !filteredSummaryLienAdj.find(
                (filAdj) => filAdj.description === adj.description,
              )
            ) {
              matchedCustomAdjustments.push(adj);
            } else {
              unmatchedAdjustments.push(adj);
            }
          });
          // we are mapping adjustments of type 'Adjustment' from origin to CustomPriceAdjustment adjustment
          matchedCustomAdjustments.forEach((adj) => {
            const index = unmatchedAdjustments.findIndex(
              (orgAdj) =>
                orgAdj?.adjustmentType ===
                  AdjustmentType.CustomPriceAdjustment &&
                orgAdj?.description === adj?.description,
            );

            if (index !== -1) {
              unmatchedAdjustments[index].rate = adj?.rate || null;
            } else {
              unmatchedAdjustments.push({
                adjustmentType: AdjustmentType.CustomPriceAdjustment,
                description: adj?.description,
                price: null,
                rate: adj?.rate || null,
              });
            }
          });
          const adjustmentsToAdd = unmatchedAdjustments.filter(
            (adj) =>
              adj.adjustmentType === AdjustmentType.ReLockFeeAdjustment ||
              adj.adjustmentType === AdjustmentType.CustomPriceAdjustment ||
              adj.adjustmentType === AdjustmentType.CorporatePriceConcession ||
              adj.adjustmentType === AdjustmentType.BranchPriceConcession,
          );
          const proposedAdjustments =
            summary.lien.adjustments.concat(adjustmentsToAdd);
          // we are removing parseRateAdjustmentsValidate function (EPPS-96880) after discussing with Alex,Evan
          // as it is adding the SRP to base price and rate, showing/hiding, adding/sum of SRP/LO
          // adjustments to base should be handled by API
          const compareResult = getAdjustmentCompare(
            unmatchedAdjustments,
            proposedAdjustments,
          );
          dispatch(setCompareDataAction(compareResult));
        }
        dispatch(closeLoader());
      } else {
        navigate(PATH_SEARCH_RESULTS);
      }
    } catch {
      dispatch(closeLoader());
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }),
      );
    }
  };

export const getBuysideDataAction =
  (navigate: NavigateFunction, isZeroBasedPricing: boolean) =>
  async (dispatch, state) => {
    const loanData = get(state(), 'EPPS.loanInfo', {});
    const lockRequests = get(
      state(),
      'EPPS.originalLoanInformation.lockRequests',
      [],
    );
    const originalEffectiveDate = get(
      state(),
      'EPPS.originalLoanInfo.relockRequest.effectiveDate',
      [],
    );
    const snapshotProgramName = getSnapshotProgramInfo(lockRequests);
    const { relockRequest, loanInformation, additionalLockInfo } =
      loanData || {};
    const { target } = loanInformation || {};
    const { specificRateDataID, specificProgramID } = relockRequest || {};
    const rate = target <= 20 ? target : null;
    const loanFieldComparisson = Object.values(
      additionalLockInfo?.lockCompareResults || {},
    ).flat() as any[];
    const rateChange = loanFieldComparisson.find(({ id }) => id === 'rate');
    const { proposed: proposedRate, current } = rateChange || {};
    const changedNoteRate = !!proposedRate;
    const { lockEffectiveDate } = Session.get(ALL_SUMMARY_PARAMS) || {};
    const isSellSide = Session.get(IS_SELLSIDE);
    const isBuySide = Session.get(IS_BUYSIDE);
    loanData.loanInformation.target = convertPriceToParBased(
      isZeroBasedPricing,
      loanData.loanInformation.target,
      true,
      loanData,
    );
    // In SELLSIDE: stash the origin loanId (buyside loanId returned by
    // getOrigin) in Session before nulling it for the rates request, so the
    // adjustments fetch hits the loan the adjustments were saved against.
    // Only capture on the first run, since the buyside loanId is invariant
    // for a given loan, and Redux's loanId will be the restored sellside id
    // on subsequent runs (would otherwise overwrite with the wrong value).
    // Then clear effectiveDate and loanId so the rates call is issued with
    // loanId=null (backend assigns a new sellside loanId, restored below).
    if (isSellSide) {
      if (loanData.loanInformation.loanId && !Session.get(BUYSIDE_LOAN_ID)) {
        Session.set(BUYSIDE_LOAN_ID, loanData.loanInformation.loanId);
      }
      loanData.relockRequest.effectiveDate = null;
      loanData.loanInformation.loanId = null;
      loanData.excludeExceptions = true;
      dispatch(setLoanDataValue(null, 'relockRequest.effectiveDate') as any);
      dispatch(setLoanDataValue(null, 'loanInformation.loanId') as any);
    }
    if (originalEffectiveDate && loanData.relockRequest?.effectiveDate) {
      const initialDate = new Date(originalEffectiveDate);
      const updatedDate = new Date(loanData.relockRequest.effectiveDate);
      if (initialDate.getTime() !== updatedDate.getTime()) {
        loanData.wcpRequest = null;
      }
    }
    try {
      dispatch(openLoader('Loading'));
      kpiStart(KPI.SelectBuySellSide, { side: isSellSide ? 'sell' : 'buy' });
      const historicalPricing = await getBuySideQualification(
        'historical',
        loanData,
        dispatch,
      );
      loanData.loanInformation.target = convertPriceToParBased(
        isZeroBasedPricing,
        loanData.loanInformation.target,
        false,
        loanData,
      );

      // In SELLSIDE: ensure loanId is populated from rates response
      if (isSellSide && loanData.loanInformation.loanId === null) {
        loanData.loanInformation.loanId = historicalPricing?.loanId;
        dispatch(
          setLoanDataValue(
            historicalPricing?.loanId,
            'loanInformation.loanId',
          ) as any,
        );
      }

      const {
        program: historicalProgram,
        rateDataID: historicalRateDataID,
        status: historicalStatus,
      } = checkPricingStatus(
        specificProgramID,
        specificRateDataID,
        isBuySide ? current : proposedRate || rate,
        historicalPricing,
        changedNoteRate,
      );
      dispatch(
        setLockedProgramData({
          programName: historicalProgram?.program || snapshotProgramName,
          programID: specificProgramID,
          notes: historicalProgram?.notes?.replace(/\{.*?\}/g, '') || '',
          status: historicalStatus,
          modalOpen: true,
        }),
      );

      if (historicalStatus === 'eligible') {
        const lockedRateOnSheet = historicalProgram.rateDetails?.some(
          ({ rateDataID }) => rateDataID === specificRateDataID,
        );
        if (isSellSide && !lockedRateOnSheet) {
          loanData.relockRequest.specificRateDataID = historicalRateDataID;
          dispatch(
            setLoanDataValue(
              historicalRateDataID,
              'relockRequest.specificRateDataID',
            ) as any,
          );
        }
        dispatch(selectProduct(historicalProgram));
        const loanPrograms = get(state(), 'EPPS.loanPrograms.loanPrograms', []);
        const selectedMIAmount = get(
          state(),
          'epps.lockSummary.selectedMIAmount',
          null,
        );
        const historicalSummary = getAppConfigValue<boolean>(
          'partner-ui.debugBuyside',
        )
          ? rateSelectorBuySide
          : await getRateSelector(
              dispatch,
              loanData,
              loanPrograms,
              selectedMIAmount,
              historicalPricing?.loanPrograms,
            );
        dispatch(setRateSelectorAction(historicalSummary));

        const { price: historicalPrice, qualifiedRate: originalRate } =
          historicalSummary?.lien || {};

        const {
          program: currentProgram,
          rateDataID: newRateDataID,
          status: currentStatus,
        } = checkPricingStatus(
          specificProgramID,
          specificRateDataID,
          Number(originalRate),
          historicalPricing,
          false,
        );

        if (currentStatus === 'eligible') {
          const { price: currentPrice } =
            currentProgram?.rateDetails?.find(
              ({ rateDataID }) => rateDataID === newRateDataID,
            ) || {};
          const priceDifference =
            typeof currentPrice === 'number' &&
            historicalPrice.toFixed(3) !== (100 - currentPrice).toFixed(3);
          if (priceDifference) {
            let currentSummary;
            if (lockEffectiveDate != undefined && lockEffectiveDate !== '') {
              const newLoanData = {
                ...loanData,
                relockRequest: {
                  ...relockRequest,
                  effectiveDate: '',
                  specificRateDataID: newRateDataID,
                },
              };
              const loanPrograms = get(
                state(),
                'EPPS.loanPrograms.loanPrograms',
                [],
              );

              currentSummary = await getRateSelector(
                dispatch,
                newLoanData,
                loanPrograms,
                undefined,
                historicalPricing?.loanPrograms,
              );
            } else {
              // avoid unnecessary call to getRateSelector as we are sendingh blank effective and getting current price for historical
              currentSummary = historicalSummary;
            }
            dispatch(setCurrentRateSelectorAction(currentSummary));
            const compareResult = getAdjustmentCompare(
              historicalSummary?.lien?.adjustments,
              currentSummary?.lien?.adjustments,
            );
            dispatch(setCompareDataAction(compareResult));
          }
        }
        dispatch(closeLoader());
        kpiEnd(KPI.SelectBuySellSide, { side: isSellSide ? 'sell' : 'buy' });
      } else {
        kpiEnd(KPI.SelectBuySellSide, {
          side: isSellSide ? 'sell' : 'buy',
          outcome: 'error',
        });
        navigate(PATH_SEARCH_RESULTS);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('getBuysideDataAction.error', err);
      dispatch(closeLoader());
      kpiEnd(KPI.SelectBuySellSide, {
        side: isSellSide ? 'sell' : 'buy',
        outcome: 'error',
      });
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }),
      );
    }
  };

export const getSettingAction = () => async (dispatch) => {
  try {
    dispatch(openLoader('Loading'));
    const settings = await getEncompassSettings();
    if (settings && !settings.code)
      dispatch(setSettings(settings.adjustmentsSettings));
  } catch {
    dispatch(
      openGlobalErrorModalAction({
        onCloseCallback: () => Common.closeApplication(),
      }),
    );
  } finally {
    dispatch(closeLoader());
  }
};

export const saveLoanAction = (rateLockContract) => async (dispatch, state) => {
  dispatch(openLoader('Saving Encompass Elements'));
  try {
    const summaryDetails = get(state(), 'epps.lockSummary.rateSelector', {});
    const originalLoanInfo = get(state(), 'epps.origin.originalLoanData', {});
    const getCustomFields = get(
      state(),
      'EPPS.loanInfo.lockRequestAdditionalFields',
      [],
    );
    const getOriginSource = Session.get(ORIGIN_SOURCE) || '';
    const customFields: any[] = [];

    if (!isEmpty(getCustomFields)) {
      getCustomFields.map((item) => {
        return customFields.push({
          fieldId: item.fieldId,
          value: item.value,
        });
      });
    }

    const transactionId = await epcService.createTransaction({
      request: {
        type: 'SUBMIT_LOAN',
        options: {
          actionType: 'SubmitRates',
          comments: summaryDetails?.loan?.comments,
          optionId: summaryDetails?.optionId,
          loanElements: [],
          customFields,
          sourceApplicationForm: getOriginSource,
        },
      },
    });

    if (transactionId) {
      const getEpcTransaction = await Common.getEpcTransaction(transactionId);
      if (getEpcTransaction.status === 'completed') {
        await Common.refreshGetOrigin();
        const diffELents = await Common.getUpdatedfield({
          loan: { ...originalLoanInfo },
          rateLockInput: {
            lenderFeeWaiverOption: rateLockContract.lenderFeeWaiver,
            noClosingCostOption: rateLockContract.noClosingCost,
          },
          sourceApplicationForm: getOriginSource,
        });
        if (!isEmpty(diffELents)) {
          dispatch(setDiffElementsAction(diffELents));
        } else Common.closeApplication();
      } else if (
        getEpcTransaction.summary === 'Conflict' ||
        getEpcTransaction.summary === 'Bad Request'
      ) {
        dispatch(
          setStatusFail({
            error: true,
            code: getEpcTransaction.code,
            message: truncateText(getEpcTransaction.details, 300),
          }),
        );
      } else if (
        getEpcTransaction.status === 'failed' &&
        getEpcTransaction.code === 'EBS-912'
      ) {
        dispatch(
          setStatusFail({
            error: true,
            code: getEpcTransaction.code,
            message: getEpcTransaction.details,
          }),
        );
      } else if (
        getAppConfigValue<boolean>('partner-ui.showDebugInformation')
      ) {
        dispatch(
          openGlobalErrorModalAction({
            errorMessage: getEpcTransaction.summary,
            title: 'Transaction Failed',
          }),
        );
      }
    } else throw new Error();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    dispatch(
      openGlobalErrorModalAction({
        onCloseCallback: () => Common.closeApplication(),
      }),
    );
  } finally {
    dispatch(closeLoader());
  }
};

export const floatPricingAction =
  (rateLockInput) => async (dispatch, state) => {
    dispatch(openLoader('Floating Pricing'));
    try {
      const summaryDetails = get(state(), 'epps.lockSummary.rateSelector', {});
      const originalLoanInfo = get(state(), 'epps.origin.originalLoanData', {});
      const loanInfo = get(state(), 'EPPS.loanInfo', []);
      const loanProperties = get(state(), 'EPPS.loanProperties', []);
      const getCustomFields = loanInfo?.lockRequestAdditionalFields || [];
      const loanData = loanInfo?.loanInformation || [];

      const pricingOriginSource = Session.get(ORIGIN_SOURCE) || '';
      const customFields: any[] = [];

      if (!isEmpty(getCustomFields)) {
        getCustomFields.map((item) => {
          return customFields.push({
            fieldId: item.fieldId,
            value: item.value,
            dataType: item.dataType,
          });
        });
      }

      const transactionId = await epcService.createTransaction({
        request: {
          type: 'FLOAT',
          options: {
            actionType: 'SubmitRates',
            sourceApplicationForm: pricingOriginSource,
            optionId: summaryDetails?.optionId,
            customFields,
            loanElements: [],
          },
        },
      });

      if (transactionId) {
        const getEpcTransaction = await Common.getEpcTransaction(transactionId);
        if (getEpcTransaction.status === 'completed') {
          await Common.refreshGetOrigin();
          const diffELents = await Common.getUpdatedfield({
            loan: { ...originalLoanInfo },
            rateLockInput,
            sourceApplicationForm: pricingOriginSource,
          });
          if (!isEmpty(diffELents)) {
            dispatch(setDiffElementsAction(diffELents));
          } else Common.closeApplication();
        } else if (
          getEpcTransaction.summary === 'Conflict' ||
          getEpcTransaction.summary === 'Bad Request'
        ) {
          dispatch(
            setStatusFail({
              error: true,
              code: getEpcTransaction.code,
              message: truncateText(getEpcTransaction.details, 300),
            }),
          );
        } else if (
          getEpcTransaction.status === 'failed' &&
          getEpcTransaction.code === 'EBS-912'
        ) {
          dispatch(
            setStatusFail({
              error: true,
              code: getEpcTransaction.code,
              message: getEpcTransaction.details,
            }),
          );
        } else if (
          getAppConfigValue<boolean>('partner-ui.showDebugInformation')
        ) {
          dispatch(
            openGlobalErrorModalAction({
              errorMessage: getEpcTransaction.summary,
              title: 'Transaction Failed',
            }),
          );
        }
      } else throw new Error();
    } catch (err) {
      console.error('Unhandled floatPricing error', err);
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }),
      );
    } finally {
      dispatch(closeLoader());
    }
  };

export const requestLockAction =
  (EPCActionType, actionType, rateLockContract) => async (dispatch, state) => {
    dispatch(openLoader('Requesting Lock'));
    kpiStart(KPI.RequestLock);
    let outcome = 'error';
    try {
      const summaryDetails = get(state(), 'epps.lockSummary.rateSelector', {});
      const sellSideBestPrice =
        summaryDetails?.loan?.autolocked?.sellSideBestPrice;
      const originalLoanInfo = get(state(), 'epps.origin.originalLoanData', {});
      const selectedMIAmount = get(
        state(),
        'epps.lockSummary.selectedMIAmount',
        null,
      );
      const getCustomFields = get(
        state(),
        'EPPS.loanInfo.lockRequestAdditionalFields',
        [],
      );
      const isLockRequestUnderOnrp = get(
        state(),
        'lockDeskStatus.isLockRequestUnderOnrp',
        {},
      );
      const onrpFields = get(state(), 'EPPS.loanInfo.loanInformation', []);
      const { loanChannel, firstMortgageAmount } = onrpFields;
      const getOriginSource = Session.get(ORIGIN_SOURCE) || '';
      const customFields: any[] = [];

      if (!isEmpty(getCustomFields)) {
        getCustomFields.map((item) => {
          return customFields.push({
            fieldId: item.fieldId,
            value: item.value,
          });
        });
      }

      let sellSideBestSummary;
      if (sellSideBestPrice) {
        const { loanPrograms, loanId } = get(state(), 'EPPS.loanPrograms', {});
        const loanData = get(state(), 'EPPS.loanInfo', {});
        const { rate: selectedRate, loanTerm: selectedTerm } =
          summaryDetails?.lien ?? {};

        const {
          bestProgramID,
          bestRateID,
          lockEffectiveDate,
          wcpPolicyApplied,
        } = findBestProgram(loanPrograms, selectedRate, selectedTerm);
        Session.set(ALL_SUMMARY_PARAMS, {
          loanId,
          programId: bestProgramID,
          rateDataId: bestRateID,
          lockEffectiveDate,
          wcpPolicyApplied,
        });

        sellSideBestSummary = await getRateSelector(
          dispatch,
          loanData,
          loanPrograms,
          selectedMIAmount,
        );
      }
      const buysideAdjustments = Object.values(
        get(state(), 'epps.origin.buySideAdjustments') || {},
      ).flat(1);

      const loanPrograms = get(state(), 'EPPS.loanPrograms.loanPrograms', []);
      const rateDataId = summaryDetails.lien?.rateDataId;
      const matchingRateData = (loanPrograms ?? [])
        .map((program) => program.rateDetails)
        .flat(1)
        .find((rateDetail) => rateDetail.rateDataID === rateDataId);
      const updateLockDate = matchingRateData?.updateLockDate;
      summaryDetails.updateLockDate = updateLockDate;

      const originalRateLock = get(
        state(),
        'EPPS.originalLoanInformation.rateLock',
        {},
      );
      const originalAdjustments = Session.get(IS_SELLSIDE)
        ? originalRateLock?.sellSideAdjustments || []
        : buildBuysideAdjustmentsWithConcessions(originalRateLock);

      const transactionId = await epcService.createTransaction({
        request: {
          type: EPCActionType,
          options: {
            actionType,
            comments: summaryDetails?.loan?.comments,
            optionId: summaryDetails?.optionId,
            customFields,
            buysideAdjustments,
            ...(actionType === 'ActiveLockUpdate' && { originalAdjustments }),
            loanElements: [],
            sourceApplicationForm: getOriginSource,
            ...(sellSideBestSummary
              ? { sellSideDetails: sellSideBestSummary }
              : {}),
          },
        },
      });

      if (transactionId) {
        const getEpcTransaction = await Common.getEpcTransaction(transactionId);
        outcome = getEpcTransaction.status;
        if (getEpcTransaction.status === 'completed') {
          if (isLockRequestUnderOnrp) {
            await Common.getOnrpAmount(
              loanChannel === 0 ? 1 : loanChannel,
              firstMortgageAmount,
            );
          }
          await Common.refreshGetOrigin();

          const diffELents = await Common.getUpdatedfield({
            loan: { ...originalLoanInfo },
            rateLockInput: {
              lenderFeeWaiverOption: rateLockContract.lenderFeeWaiver,
              noClosingCostOption: rateLockContract.noClosingCost,
            },
            sourceApplicationForm: getOriginSource,
          });
          if (!isEmpty(diffELents)) {
            dispatch(setDiffElementsAction(diffELents));
          } else Common.closeApplication();
        } else if (
          getEpcTransaction.summary === 'Conflict' ||
          getEpcTransaction.summary === 'Bad Request'
        ) {
          dispatch(
            setStatusFail({
              error: true,
              code: getEpcTransaction.code,
              message: truncateText(getEpcTransaction.details, 300),
            }),
          );
        } else if (
          getEpcTransaction.status === 'failed' &&
          getEpcTransaction.code === 'EBS-912'
        ) {
          dispatch(
            setStatusFail({
              error: true,
              code: getEpcTransaction.code,
              message: getEpcTransaction.details,
            }),
          );
        } else if (
          getAppConfigValue<boolean>('partner-ui.showDebugInformation')
        ) {
          dispatch(
            openGlobalErrorModalAction({
              errorMessage: getEpcTransaction.summary,
              title: 'Transaction Failed',
            }),
          );
        }
      } else throw new Error();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      dispatch(
        openGlobalErrorModalAction({
          onCloseCallback: () => Common.closeApplication(),
        }),
      );
    } finally {
      dispatch(closeLoader());
      kpiEnd(KPI.RequestLock, { outcome });
    }
  };

export const selectPriceAction = (newSelection) => {
  return createAction(SELECT_PRICE)(newSelection);
};

const NON_QM_STANDARD_PRODUCT = 6;

export const getWriteBackMortgageType = (rawType, standardProducts) => {
  if (
    rawType === 'Other' &&
    standardProducts?.includes(NON_QM_STANDARD_PRODUCT)
  ) {
    return 'Conventional';
  }
  return rawType;
};

export const buildEpcLoanObject = (opts) => {
  const {
    summaryDetails,
    pricingOriginSource,
    originalLoanInfo,
    loanData,
    customFields,
    loanProperties,
  } = opts;

  const isFromSecondaryLock =
    pricingOriginSource.toLowerCase() === 'secondarylock';
  const loanPurposeType = getLoanPurposeType(
    summaryDetails?.loan?.purposeId,
    summaryDetails?.loan?.gseRefinancePurposeType,
  );

  // TODO(ngen): baseMarginRate was defined here, but never defined inside getBasePricingValues. Would always be undefined.
  const { baseRate, basePrice, srp } = getBasePricingValues(
    summaryDetails,
    isFromSecondaryLock,
  );

  const { requestPrepayPenalty, requestPenaltyTerm } =
    getPrepaymentPenaltyTerm(summaryDetails);

  const propTypeId = summaryDetails?.loan?.property?.typeId ?? 1;
  const propertyType = getPropertyType(propTypeId);
  const {
    subjectPropertyCondotelIndicator,
    subjectPropertyNonWarrantableProjectIndicator,
  } = getIsCondotel(summaryDetails, propertyType);

  const mortgageType = summaryDetails?.loan?.mortgageType;
  const qualificationTypeId = summaryDetails?.loan?.qualificationTypeId;

  const { requestMarginRate, requestPriceRateRequested } =
    getCalculatedAdjustmentValues(summaryDetails);

  let gfe2010FeeType = 'DiscountPoint';
  let gfe2010FeeIndex = 1;
  if (summaryDetails?.lien?.commission >= 0) {
    gfe2010FeeType = 'LenderCompensationCredit';
    gfe2010FeeIndex = 2;
  }

  const usageId = summaryDetails?.loan?.property?.usageId;

  const commision = summaryDetails?.lien?.commission;
  const shouldSetFundingValues =
    summaryDetails?.lien?.va ||
    summaryDetails?.lien?.fha ||
    summaryDetails?.lien?.usda;
  // EPPS-105092/EPPS-106358: FHA/VA/USDA paid in cash under $1 means the fee is
  // fully financed, so leave the paid-in-cash lock off and let Encompass derive it.
  const lockMipPaidInCash = !(
    (summaryDetails?.lien?.fha ||
      summaryDetails?.lien?.va ||
      summaryDetails?.lien?.usda) &&
    summaryDetails?.loan?.feeAmountPaidinCash < 1
  );
  // With the lock off Encompass derives 3045/3047 itself, so it needs the
  // fee factor on 3044. Pricing echoes loanInformation, fall back to loanData.
  const govtFeeFactor =
    summaryDetails?.loan?.govtFeeFactor ?? loanData?.govtFeeFactor;
  const sendGovtFeeFactor = !lockMipPaidInCash && !isNil(govtFeeFactor);

  const propertyValueParams = getPropertyValues(summaryDetails, loanProperties);

  const baseLoanAmount = summaryDetails?.loan?.baseLoanAmount;
  // EPPS-82015: on buyside product change the UI-recomputed loanData fee is
  // stale, so trust the pricing response first and fall back to loanData.
  const feeAmountFinanced =
    summaryDetails?.loan?.feeAmountFinanced ??
    (loanData && (loanData as any).feeAmountFinanced) ??
    0;
  const totalLoanAmount = Number(baseLoanAmount) + Number(feeAmountFinanced);
  const isHeloc = mortgageType === 'HELOC';
  const extensions = (customFields || []).reduce((acc, field) => {
    let value = field.value;
    if (field.dataType === 'X') {
      value = field.value === 'True';
    } else if (field.dataType === 'YN') {
      value = field.value === 'Y';
    }
    return {
      ...acc,
      [field.fieldId]: value,
    };
  }, {});

  const epcLoan: DeepPartial<FloatResponse.FloatResponse> = {
    loan: {
      miAndFundingFeeFinancedAmount: feeAmountFinanced,
      gfe: {
        fundingAmount: feeAmountFinanced,
        lockField: lockMipPaidInCash,
      },
      mortgageInsurancePremiumUpfrontFactorPercent:
        summaryDetails?.loan?.fhaVaFeeFactor,
      extensions,
      basePrice,
      baseRate,
      baseLoanAmount: totalLoanAmount,
      borrowerRequestedLoanAmount: isHeloc
        ? summaryDetails?.loan?.maxLoanAmountMaxLine
        : baseLoanAmount, // is base loan amount
      baseMarginRate: undefined,
      correspondentCommitmentType: summaryDetails?.loan?.commitmentType,
      correspondentDeliveryType: summaryDetails?.loan?.deliveryType,
      closingCost: {
        gfe2010: {
          gfe2010Fees: [
            {
              gfe2010FeeParentType: 'Section800LOCompensation',
              gfe2010FeeType: 'LenderCompensationCredit',
              gfe2010FeeIndex: 2,
              rate: commision >= 0 ? Math.abs(commision) : 0,
            },
            {
              gfe2010FeeParentType: 'Section800LOCompensation',
              gfe2010FeeType: 'DiscountPoint',
              gfe2010FeeIndex: 1,
              rate: commision >= 0 ? 0 : Math.abs(commision),
            },
          ],
        },
      },
      contacts: [
        {
          contactType: 'LENDER_INVESTOR',
          name: summaryDetails?.lien?.lenderName || null,
        },
        {
          contactType: 'INVESTOR',
          name: summaryDetails?.lien?.lenderName || null,
        },
      ],
      firstSubordinateLienAmount:
        summaryDetails?.lien?.firstSubordinateLienAmount,
      helocTeaserRate: summaryDetails?.lien?.introRate?.toString(),
      loanProductData: {
        armDisclosureType: summaryDetails?.lien?.armIndex
          ? summaryDetails?.lien?.maventIndexCode?.toString()
          : null,
        balloonLoanMaturityTermMonthsCount:
          summaryDetails?.lien?.loanTermMonths ?? 0,
        indexCurrentValuePercent: summaryDetails?.lien?.armIndexValue,
        indexDate: summaryDetails?.lien?.indexRetrievedDate
          ? new Date(summaryDetails?.lien?.indexRetrievedDate)
              .toISOString()
              .split('T')[0]
          : null,
        indexMarginPercent: summaryDetails?.lien?.armMargin,
        lienPriorityType:
          qualificationTypeId === 2 ? 'SecondLien' : 'FirstLien',
        prepaymentPenaltyIndicator: requestPrepayPenalty === 'Y',
        ...getArmAdjustmentFields(summaryDetails),
        qualifyingRatePercent:
          mortgageType === 'HELOC' ? null : summaryDetails?.lien?.qualifiedRate,
        gsePropertyType: propertyType,
      },
      lockdays: summaryDetails?.lien?.userLockDays,
      loanAmortizationTermMonths: summaryDetails?.loan?.amortizationTermMonths,
      loanAmortizationType: summaryDetails?.loan?.amortizationType,
      loanProgramName: summaryDetails?.lien?.userProgramName,
      mortgageType,
      noClosingCostOption: summaryDetails?.loan?.noClosingCost,
      property: {
        financedNumberOfUnits: getFinancedNumberOfUnits(summaryDetails),
        loanPurposeType,
        condotelIndicator: loanData?.condotel,
        nonwarrantableProjectIndicator: loanData?.non_warrantable_Condo,
      },
      ...propertyValueParams,
      rateLock: {
        ...propertyValueParams,
        baseLoanAmount: isHeloc
          ? summaryDetails?.loan?.maxLoanAmountMaxLine
          : baseLoanAmount,
        borrowerRequestedLoanAmount: totalLoanAmount,
        borrLenderPaid: getCompensationModel(
          summaryDetails?.loan?.compensationModel,
        ),
        buySidePriceRate: isFromSecondaryLock
          ? summaryDetails?.lien?.price
          : null,
        firstTimeHomebuyersIndicator:
          summaryDetails?.loan?.borrowers?.[0]?.finance?.firstTimeHomeBuyers,
        floatDateUpdatedOn: new Date().toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
        }),
        fundingAmount: summaryDetails?.loan?.feeAmount, // This is the whole fee amount. Not just the financed portion.
        initialAdvanceAmount: getInitialAdvAmount(
          summaryDetails,
          mortgageType,
          qualificationTypeId,
        ),
        // just cash here
        mipPaidInCash: summaryDetails?.loan?.feeAmountPaidinCash,
        penaltyTerm: requestPenaltyTerm,
        planCode: summaryDetails?.lien?.programCode,
        pricingUpdated: new Date().toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
        }),
        propertyUsageType: getPropertyUsageType(usageId),
        lenderFeeWaiverOption: summaryDetails?.loan?.lenderFeeWaiver,
        loanDocumentationType: getDocumentationType(summaryDetails),
        lockField: lockMipPaidInCash, // fundingAmount and mipPaidInCash will not populate unless this is true
        ...(sendGovtFeeFactor
          ? { fhaUpfrontMiPremiumPercent: govtFeeFactor }
          : {}),
        lockRequestAdjustments: getEpcRateLockAdjustments(summaryDetails),
        requestComments: summaryDetails?.loan?.comments ?? '',
        requestImpoundWaived: summaryDetails?.lien?.waiveEscrow
          ? 'Waived'
          : 'Not Waived',
        requestMarginRate,
        requestPenaltyTerm,
        requestPrepayPenalty,
        requestPriceRate: basePrice,
        requestPriceRateRequested,
        requestProgramNotes: summaryDetails?.lien?.note?.join(','),
        requestRate: baseRate,
        requestRateSheetId: isFromSecondaryLock
          ? null
          : summaryDetails?.lien?.rateSheetId,
        requestStartingAdjPoint: setSignificantDigits(
          summaryDetails?.lien?.startingAdjustedPrice,
          3,
        ),
        requestStartingAdjRate: isFromSecondaryLock
          ? null
          : setSignificantDigits(summaryDetails?.lien?.startingAdjustedRate, 3),
        requestUnDiscountedRate: setSignificantDigits(
          summaryDetails?.lien?.undiscountedRate,
          3,
        ),
        secondSubordinateAmount: summaryDetails?.lien?.secondSubordinateAmount,
        subjectPropertyCondotelIndicator,
        subjectPropertyNonWarrantableProjectIndicator,
      },
      requestedInterestRatePercent: setSignificantDigits(
        summaryDetails?.lien?.rate,
        3,
      ),
      regulationZ: {
        lenderPaidMortgageInsuranceIndicator:
          summaryDetails?.loan?.lenderPaidMI?.toString(),
        ...getGoveFeesForUSDA(summaryDetails),
      },
      srp,
      twelveMonthMortgageRentalHistoryIndicator:
        summaryDetails?.loan?.borrowers?.[0]?.finance
          ?.demonstrateHousingPaymentHistory,
      undiscountedRate: summaryDetails?.lien?.undiscountedRate,
      ...getRiskAssessmentType(originalLoanInfo, loanData),
      ...getGoveFeesForFHAandVA(summaryDetails),
      ...getVaLoanData(loanData, originalLoanInfo),
    },
  };

  return epcLoan;
};

const getCompensationModel = (compModelEnum) => {
  const compConfig = {
    1: 'Borrower Paid',
    2: 'Lender Paid',
    3: 'Exempt Paid',
  };

  return compConfig?.[compModelEnum];
};

const getDocumentationType = (summaryDetails) => {
  const docId = summaryDetails?.lien?.documentationId;

  const docIdConfig = {
    1: 'FullDocumentation',
    2: 'NoVerificationOfStatedIncome',
    3: 'NoVerificationOfStatedIncomeOrAssets',
    4: 'NoRatio',
    5: 'NoDepositVerification',
    7: 'NoDocumentation',
    9: 'Reduced',
    10: 'LimitedDocumentation',
    11: 'NoVerificationOfStatedAssets',
  };

  return docIdConfig[docId ?? 1]; // default is full documentation
};

const getFinancedNumberOfUnits = (summaryDetails) => {
  const propTypeId = summaryDetails?.loan?.property?.typeId ?? 1;

  return (propTypeId <= 4 ? propTypeId : 1)?.toString();
};

const getInitialAdvAmount = (
  summaryDetails,
  mortgageType,
  qualificationTypeId?,
) => {
  if (
    mortgageType === 'HELOC' &&
    (qualificationTypeId === 1 || qualificationTypeId === 2)
  ) {
    return summaryDetails?.loan?.initialAdvanceAmount;
  }

  return null;
};

const getIsCondotel = (summaryDetails, propertyType) => {
  const propTypeWhitelist = [
    'Condominium',
    'HighRiseCondominium',
    'DetachedCondo',
  ];

  let subjectPropertyCondotelIndicator = false;
  let subjectPropertyNonWarrantableProjectIndicator = false;

  if (propTypeWhitelist.includes(propertyType)) {
    subjectPropertyCondotelIndicator = summaryDetails?.lien?.condotel;
    subjectPropertyNonWarrantableProjectIndicator =
      summaryDetails?.lien?.non_warrantable_condo;
  }

  return {
    subjectPropertyCondotelIndicator,
    subjectPropertyNonWarrantableProjectIndicator,
  };
};

const getPrepaymentPenaltyTerm = (summaryDetails) => {
  const ppp = toNumber(summaryDetails?.lien?.ppp);

  const ppeEnumConfig = {
    0: '',
    1: '1 Year',
    2: '2 Years',
    3: '3 Years',
    4: '4 Years',
    5: '5 Years',
  };

  const invalidText = 'NOT_VALID';
  const requestPenaltyTerm = ppeEnumConfig[ppp] ?? invalidText;
  let requestPrepayPenalty;
  if (requestPenaltyTerm !== invalidText) {
    requestPrepayPenalty = ppp === 0 ? 'N' : 'Y';
  }

  return { requestPenaltyTerm, requestPrepayPenalty };
};

const getBasePricingValues = (summaryDetails, isFromSecondaryLock) => {
  // Defensive parser: treat missing adjustment arrays as [] and missing numeric fields as 0
  const parseBase = (key, adjs) =>
    (adjs ?? []).reduce(
      (sum: number, curr: any) => sum + (Number(curr?.[key]) || 0),
      0,
    );

  const baseTypeAdjs = summaryDetails?.lien?.adjustments?.filter(
    (adj) => adj.adjustmentType === 'Base',
  );
  const baseRate = parseBase('rate', baseTypeAdjs);
  let basePrice = parseBase('price', baseTypeAdjs);
  let baseMargin = parseBase('margin', baseTypeAdjs);
  let srp;

  const srpPriceTotal = parseBase(
    'price',
    summaryDetails?.lien?.adjustments?.filter(
      (adj) => adj.adjustmentType === 'SRP',
    ),
  );

  if (!isFromSecondaryLock) {
    basePrice += srpPriceTotal;
  } else {
    srp = round(srpPriceTotal, 3);
  }

  if (!baseMargin) {
    baseMargin = summaryDetails?.lien?.armMargin;
  }

  const loCompHoldbackValue = (summaryDetails?.lien?.adjustments ?? [])
    .filter(
      (adj) =>
        adj.adjustmentType === 'Holdback' &&
        adj.description === 'L.O. Compensation',
    )
    .reduce((sum: number, curr: any) => sum + (Number(curr?.price) || 0), 0);
  basePrice += loCompHoldbackValue;

  return {
    basePrice: round(basePrice, 3),
    baseRate: round(baseRate, 3),
    baseMargin: round(baseMargin, 3),
    srp,
  };
};

const getCalculatedAdjustmentValues = (summaryDetails) => {
  const baseAdjustment = summaryDetails?.lien?.adjustments?.find(
    (adj) => adj.adjustmentType === 'Base',
  );

  const finalAdjustment = summaryDetails?.lien?.adjustments?.find(
    (adj) => adj.adjustmentType === 'Final',
  );

  // Defensive numeric extraction: default missing values to 0 to avoid NaN
  const baseMarginVal = Number(baseAdjustment?.margin) || 0;
  const finalMarginVal = Number(finalAdjustment?.margin) || 0;
  const basePriceVal = Number(baseAdjustment?.price) || 0;
  const finalPriceVal = Number(finalAdjustment?.price) || 0;

  let requestMarginRate = null;
  let requestMarginTotalAdjustment: number | null = null;
  if (summaryDetails?.lien?.arm) {
    // preserve original semantics: if baseAdjustment had no margin, leave requestMarginRate null
    requestMarginRate = baseAdjustment?.margin ?? null;
    requestMarginTotalAdjustment = finalMarginVal - baseMarginVal;
  }

  return {
    requestMarginRate,
    requestMarginTotalAdjustment,
    requestPriceTotalAdjustment: finalPriceVal - basePriceVal,
    requestPriceRateRequested: finalAdjustment?.price ?? null,
  };
};

const getEpcRateLockAdjustments = (summaryDetails) => {
  const { adjustments } = summaryDetails?.lien ?? {};
  const userRoles = Common.getUserRoles();
  const userRoleRoles = userRoles.map(({ role }) => role);
  const canViewCompensation =
    userRoleRoles.includes('View Compensation') &&
    !userRoleRoles.includes('View Profit Margin');

  const potentialAdjustmentsToSend = adjustments.filter(
    (adj) =>
      ['Adjustment', 'Holdback'].includes(adj.adjustmentType) &&
      (!canViewCompensation || adj.description !== 'L.O. Compensation'),
  );

  const epcAdjustments: Array<{
    adjustmentType: string;
    description: string;
    priceAdjustmentType: string;
    priceAdjustmentTypeIndex: number;
    rate: number;
    rateLockAdjustmentType: string;
    paidByGuid?: string;
    adjustmentReasonGuid?: string;
  }> = [];
  let priceAdjIndex = 0;
  let marginAdjIndex = 0;
  let rateAdjIndex = 0;

  for (let i = 0; i < potentialAdjustmentsToSend?.length; i += 1) {
    const adj = potentialAdjustmentsToSend[i];

    if (adj.adjustmentType === 'Adjustment') {
      if (adj.price !== null && adj.price !== undefined) {
        epcAdjustments.push({
          adjustmentType: adj.adjustmentType,
          description: adj.description,
          priceAdjustmentType: 'BasePrice',
          priceAdjustmentTypeIndex: (priceAdjIndex += 1),
          rate: adj.price,
          rateLockAdjustmentType: 'Request',
          paidByGuid: adj.paidByGuid,
          adjustmentReasonGuid: adj.adjustmentReasonGuid,
        });
      }

      if (adj.rate !== null && adj.rate !== undefined) {
        epcAdjustments.push({
          adjustmentType: adj.adjustmentType,
          description: adj.description,
          priceAdjustmentType: 'BaseRate',
          priceAdjustmentTypeIndex: (rateAdjIndex += 1),
          rate: adj.rate,
          rateLockAdjustmentType: 'Request',
        });
      }

      if (adj.margin !== null && adj.margin !== undefined) {
        epcAdjustments.push({
          adjustmentType: adj.adjustmentType,
          description: adj.description,
          priceAdjustmentType: 'BaseMargin',
          priceAdjustmentTypeIndex: (marginAdjIndex += 1),
          rate: adj.margin,
          rateLockAdjustmentType: 'Request',
        });
      }
    } else if (
      adj.adjustmentType === 'Holdback' &&
      adj.price !== null &&
      adj.price !== undefined
    ) {
      epcAdjustments.push({
        adjustmentType: 'Adjustment',
        description: adj.description,
        priceAdjustmentType: 'BasePrice',
        priceAdjustmentTypeIndex: (priceAdjIndex += 1),
        rate: adj.price,
        rateLockAdjustmentType: 'Request',
      });
    }
  }

  return epcAdjustments;
};
